import { Check, Copy, X } from "lucide-react";
import React, { useCallback, useMemo } from "react";
import { useImmer } from "use-immer";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgChip } from "@intelligems/ig-theme/IgChip";
import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { IgLink } from "@intelligems/ig-theme/IgLink";
import { IgPaper } from "@intelligems/ig-theme/IgPaper";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Box, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import {
  useGetOrg,
  useGetOrgId,
} from "@src/api/organizations/get-organization";
import { usePatchOrganization } from "@src/api/organizations/patch-organization";
import {
  refreshScriptStatus,
  useGetScriptStatus,
} from "@src/api/shops/get-script-status";
import { useDialog } from "@src/hooks/dialog";
import { useScriptVerification } from "@src/hooks/useScriptVerification";
import { AddThemeScript } from "@src/images/Icons/EnableIntelligems/AddThemeScript";
import { EnableAppEmbed } from "@src/images/Icons/EnableIntelligems/EnableAppEmbed";
import { SaveChanges } from "@src/images/Icons/EnableIntelligems/SaveChanges";
import { ThemeLiquidFile } from "@src/images/Icons/EnableIntelligems/ThemeLiquidFile";
import { ToggleOnScript } from "@src/images/Icons/EnableIntelligems/ToggleOnScript";
import { TrackedMpClickActions } from "@src/types/mixpanel";
import { contactSupport } from "@src/utils/contactSupport";
import { generateShopifyEditorUrl } from "@src/utils/generateThemeEditorLink";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { FullIgScript } from "@src/utils/intelligemsThemeSnippets";
import {
  allIntegrationGuides,
  appEmbedIntegrationGuide,
  igDirectScriptAdd,
} from "@src/utils/linkSheet";
import { mpScriptIntegration } from "@src/utils/mixpanel";
import { openNewTab } from "@src/utils/openNewTab";
import { useQueryClient } from "@tanstack/react-query";

import { StepCardsContainer } from "./StepCardsContainer";

interface IgScriptTypeSelectCardProps {
  svg: React.ReactNode;
  title: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
  isRecommended?: boolean;
}

const IgScriptTypeSelectCard = ({
  svg,
  title,
  description,
  selected,
  onSelect,
  isRecommended,
}: IgScriptTypeSelectCardProps) => {
  const theme = useTheme();
  return (
    <IgPaper
      onClick={() => onSelect()}
      isClickable={true}
      isSelected={selected}
      borderColor={"primary"}
      hoverBorderColor={"accent"}
      selectedBorderColor={"accent"}
      sx={{
        width: "300px",
        padding: "0",
        position: "relative",
        boxSizing: "border-box",
        backgroundColor: theme.palette.common.white,
        cursor: "pointer",
        "&:hover": {
          backgroundColor: theme.palette.common.white,
        },
      }}
    >
      <Grid container direction="column" spacing={1}>
        <Grid padding={0}>
          <Box sx={{ position: "relative", height: "164px" }}>
            <Box
              sx={{
                height: "20px",
                borderTopLeftRadius: "6px",
                borderTopRightRadius: "6px",
                backgroundColor: selected
                  ? theme.igBackgroundColors.accent
                  : theme.igBackgroundColors.baseLightLow,
              }}
            />
            <Grid container direction={"row"} wrap={"nowrap"}>
              <Grid>
                <Box
                  sx={{
                    width: "20px",
                    height: "100%",
                    borderTopLeftRadius: "6px",
                    borderTopRightRadius: "6px",
                    backgroundColor: selected
                      ? theme.igBackgroundColors.accent
                      : theme.igBackgroundColors.baseLightLow,
                  }}
                />
              </Grid>
              <Grid>
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "6px 0px",
                    "& img": {
                      width: "100%",
                      height: "100%",
                      display: "block",
                    },
                  }}
                >
                  {svg}
                </Box>
              </Grid>
            </Grid>

            {isRecommended && (
              <Box
                sx={{
                  position: "absolute",
                  left: 10,
                  top: 7,
                }}
              >
                <IgChip
                  size={"small"}
                  color={"success"}
                  label={"Recommended"}
                />
              </Box>
            )}
            <Box
              sx={{
                position: "absolute",
                top: "10px",
                right: "8px",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: selected
                  ? theme.igBackgroundColors.accentSolid
                  : theme.palette.common.white,
                borderRadius: "50%",
                ...(!selected && {
                  border: `1px solid ${theme.igBorders.core.default}`,
                }),
              }}
            >
              {selected && (
                <Check size={16} color={theme.palette.common.white} />
              )}
            </Box>
          </Box>
        </Grid>
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            boxShadow: "0px -4px 8px rgba(13, 18, 22, 0.1)",
          }}
        >
          <Grid marginLeft={5} marginTop={4}>
            <IgTypography fontWeight={600}>{title}</IgTypography>
          </Grid>
          <Grid marginLeft={5} mb={5}>
            <IgTypography>{description}</IgTypography>
          </Grid>
        </Box>
      </Grid>
    </IgPaper>
  );
};

export const ScriptTagDialog = () => {
  const dialog = useDialog("scriptTag");
  const patchOrganization = usePatchOrganization();
  const org = useGetOrg();
  const organizationId = useGetOrgId();
  const scriptStatusQuery = useGetScriptStatus(org.data?.org.id);
  const renderBlocking =
    useConfiguration<boolean>("enable_render_blocking")?.value ?? false;

  const theme = useTheme();
  const onCopyClick = async () => {
    await navigator.clipboard.writeText(
      FullIgScript(organizationId, renderBlocking)
    );
    setCopyClicked(true);
    setTimeout(() => setCopyClicked(false), 2000);
  };
  const [copyClicked, setCopyClicked] = useImmer(false);
  const queryClient = useQueryClient();

  const {
    verified: scriptVerified,
    failed: scriptFailed,
    triggerCheck,
    resetFailure,
  } = useScriptVerification(scriptStatusQuery, { showFailureState: true });

  const [flowStep, setFlowStep] = useImmer<0 | 1>(0);
  const step = scriptVerified ? 3 : scriptFailed ? 2 : flowStep;
  const status = "failed";
  const [enableType, setEnableType] = useImmer<string>("embed");
  const [openedShopify, setOpenedShopify] = useImmer(false);
  const [trackedMpAction, setTrackedMpActions] = useImmer<
    TrackedMpClickActions[]
  >([]);
  const yesButtonText = useMemo(() => {
    if (step === 0) {
      return "Continue";
    } else if (step === 1) {
      return "Test script";
    } else {
      return "Try another way";
    }
  }, [step, status]);

  const getScreenName = (step: number, enableType: string) => {
    if (step === 0) {
      return "Enable page";
    }

    if (enableType === "embed") {
      return "App embed page";
    }
    if (enableType === "script") {
      return "Script page";
    }
    return "";
  };
  const onMpTrack = (action: string) => {
    if (!trackedMpAction.includes(action as TrackedMpClickActions)) {
      const splitAction = action.split("-");

      mpScriptIntegration(
        splitAction.length > 0 ? splitAction[1] : "",
        splitAction[0]
      );
      setTrackedMpActions((draft) => {
        draft.push(action as TrackedMpClickActions);
        return draft;
      });
    }
  };

  const onCancelClick = useCallback(
    (step: number) => {
      if (step === 2) {
        onMpTrack(`Retest the script-${getScreenName(step, enableType)}`);
        resetFailure();
        triggerCheck();
        refreshScriptStatus(queryClient, org.data?.org.id);
      } else {
        onMpTrack(`Cancel-${getScreenName(step, enableType)}`);
        dialog.close();
      }
    },
    [step, enableType]
  );

  const onYesClick = useCallback(
    (step: number) => {
      if (step === 0) {
        onMpTrack(
          `${enableType === "script" ? "Add script to theme path" : "App embed path"}-${getScreenName(step, enableType)}`
        );
        setFlowStep(1);
      } else if (step === 2) {
        // try another way click
        onMpTrack(`Try another way-${getScreenName(step, enableType)}`);
        resetFailure();
        setFlowStep(0);
      } else {
        // TEST SCRIPT
        onMpTrack(`Test script-${getScreenName(step, enableType)}`);
        triggerCheck();
        refreshScriptStatus(queryClient, org.data?.org.id);
      }
    },
    [step]
  );

  const headerDetails = useMemo(() => {
    if (step === 0) {
      return {
        title: "Connect Intelligems",
        subtitle: "Choose how to add Intelligems to your store.",
      };
    }
    if (step === 1) {
      if (enableType === "script") {
        return {
          title: "Add Intelligems script",
          subtitle: "Follow these steps in Shopify",
        };
      } else {
        return {
          title: "Connect Intelligems with app embed",
          subtitle: "Complete these steps in your Shopify admin",
        };
      }
    }
    // failed
    if (step === 2) {
      return {
        title: "Hmm, something's not quite right",
        subtitle:
          "Unfortunately we can’t detect our script on your store theme yet. You can retest it, or try another way to enable Intelligems.",
      };
    }
    return {
      title: "",
      subtitle: "",
    };
  }, [step, enableType]);

  const screenSteps = useMemo(() => {
    if (enableType === "script") {
      return [
        {
          title: "Copy your script",
          description: "",
          button: {
            text: copyClicked ? "Copied" : "Copy script",
            startIcon: <Copy size={16} />,
            onButtonClick: () => {
              onMpTrack(`Copy script-${getScreenName(step, enableType)}`);
              void onCopyClick();
            },
          },
        },
        {
          title: "Open theme code editor in Shopify",
          description: "",
          button: {
            text: "Open Shopify",
            color: "primary" as const,
            onButtonClick: () => {
              setOpenedShopify(true);
              onMpTrack(`Open Shopify-${getScreenName(step, enableType)}`);
              const url = generateShopifyEditorUrl({
                shopId: org.data.org.shopId!,
                themeId: "current",
                redirectType: "template",
              });
              openNewTab(url);
            },
          },
        },
        {
          title: "Add script to theme",
          description: (
            <IgTypography
              variant={"body2"}
              color={theme.palette.text.secondary}
            >
              Paste the Intelligems script into the &lt;head&gt; section of the
              theme.liquid file. Need help?{" "}
              <IgLink
                onClick={() => {
                  openNewTab(igDirectScriptAdd, "Add Script to Theme");
                }}
                variant={"body2"}
              >
                See our guide.
              </IgLink>{" "}
            </IgTypography>
          ),
          image: <ThemeLiquidFile />,
        },
      ];
    } else {
      return [
        {
          title: "Open Shopify admin",
          description: "Keep both windows open.",
          button: {
            text: "Open Shopify",
            color: "primary" as const,
            onButtonClick: () => {
              setOpenedShopify(true);
              onMpTrack(`Open Shopify-${getScreenName(step, enableType)}`);
              openNewTab(
                `${
                  org.data.org.shopId
                }/admin/themes/current/editor?context=apps&activateAppId=${
                  import.meta.env.VITE_SHOPIFY_THEME_BLOCKS_UUID
                }/intelligems-script`,
                undefined,
                true
              );
            },
          },
        },
        {
          title: "Verify Intelligems is toggled on",
          description: "It should be enabled by default.",
          image: <ToggleOnScript />,
        },
        {
          title: "Save changes",
          description: "Click “Save” in the top right to apply.",
          image: <SaveChanges />,
        },
      ];
    }
  }, [enableType, copyClicked]);

  return (
    <IgDialog
      padding={"24px"}
      sx={{
        "& .MuiDialogActions-root": {
          padding: "0px !important",
          height: "36px",
        },
        "& .MuiButton-root.Mui-disabled": {
          color: theme.palette.common.white,
        },
      }}
      yesButtonDisabled={!openedShopify && step === 1}
      yesButtonDisabledTooltip={
        !openedShopify && step === 1
          ? "Complete the steps above to test your script."
          : ""
      }
      yesButtonText={yesButtonText}
      shouldNotClose={true}
      hideActions={step === 3}
      learnMoreButton={
        step === 3 || step === 2 ? undefined : (
          // <Grid size={4}>
          <IgButton
            color={"link"}
            onClick={() => {
              onMpTrack(`Learn more-${getScreenName(step, enableType)}`);
              openNewTab(
                step === 0
                  ? allIntegrationGuides
                  : enableType === "script"
                    ? igDirectScriptAdd
                    : appEmbedIntegrationGuide,
                "Adding Script to Theme"
              );
            }}
          >
            Learn more
          </IgButton>
          // </Grid>
        )
      }
      handleNoClick={() => {
        onCancelClick(step);
      }}
      title={""}
      handleClose={() => {
        if (step === 3) {
          onMpTrack("Close script install success modal");
        }
        setFlowStep(0);
        dialog.close();
      }}
      dontUseDialogContent={true}
      slotProps={{
        paper: {
          sx: {
            width: step === 3 ? "500px" : "672px",
            maxWidth: "672px",
          },
        },
      }}
      noButtonText={step === 2 ? "Retest the script" : "Cancel"}
      handleYesClick={() => {
        onYesClick(step);
      }}
      showDialog={dialog.isOpen}
      showCloseIcon={false}
      loading={patchOrganization.isPending || scriptStatusQuery.isFetching}
      rawContent={
        <Stack direction={"column"} spacing={6}>
          {step === 3 ? (
            <Stack direction={"column"} spacing={1} textAlign={"center"}>
              <Grid
                container
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                wrap={"nowrap"}
              >
                <Grid />
                <Grid
                  sx={{ width: "150px", height: "150px", marginLeft: "50px" }}
                >
                  <DotLottieReact
                    src={"/lottie/script_status_check_complete.lottie"}
                    loop={true}
                    autoplay={true}
                    renderConfig={{
                      autoResize: true,
                    }}
                  />
                </Grid>
                <Grid>
                  <IgIconButton
                    onClick={() => {
                      onMpTrack("Close script install success modal");
                      dialog.close();
                    }}
                  >
                    <X size={24} />
                  </IgIconButton>
                </Grid>
              </Grid>

              <IgTypography variant={"h1"}>You're ready to go!</IgTypography>

              <Grid container justifyContent={"center"}>
                <IgTypography
                  sx={{ width: "400px" }}
                  color={theme.palette.text.secondary}
                >
                  We’ve confirmed that the script is on your store theme and
                  working as intended. You can close this window and start using
                  Intelligems.
                </IgTypography>
              </Grid>
            </Stack>
          ) : (
            <>
              <Stack direction={"column"}>
                <Grid
                  container
                  alignItems={"center"}
                  direction={"row"}
                  rowSpacing={1}
                  justifyContent={"space-between"}
                >
                  <Grid>
                    <IgTypography variant={"h3"} fontWeight={600}>
                      {headerDetails.title}
                    </IgTypography>
                  </Grid>
                  <Grid>
                    <IgIconButton size={"small"} onClick={() => dialog.close()}>
                      <X size={24} />
                    </IgIconButton>
                  </Grid>
                </Grid>

                <IgTypography
                  sx={{ width: "550px" }}
                  color={theme.palette.text.secondary}
                >
                  {headerDetails.subtitle}
                </IgTypography>
              </Stack>
              {step === 0 ? (
                <Grid mb={6} container direction={"row"} columnSpacing={6}>
                  <Grid>
                    <IgScriptTypeSelectCard
                      description={"Quick, no-code setup"}
                      title={"Use app embed"}
                      isRecommended={true}
                      selected={enableType === "embed"}
                      onSelect={() => {
                        setEnableType("embed");
                      }}
                      svg={<EnableAppEmbed />}
                    />
                  </Grid>
                  <Grid>
                    <IgScriptTypeSelectCard
                      description={"Direct installation for more control"}
                      title={"Add script to theme code"}
                      selected={enableType === "script"}
                      onSelect={() => {
                        setEnableType("script");
                      }}
                      svg={<AddThemeScript />}
                    />
                  </Grid>
                </Grid>
              ) : step === 1 ? (
                <Grid mb={6}>
                  <StepCardsContainer steps={screenSteps} />
                </Grid>
              ) : (
                <Grid mb={6}>
                  <IgTypography color={theme.palette.text.secondary}>
                    For more help, please{" "}
                    <IgLink
                      onClick={() => {
                        openNewTab(
                          allIntegrationGuides,
                          "Adding Intelligems Script to your Theme"
                        );
                      }}
                    >
                      visit our help center
                    </IgLink>{" "}
                    or reach out to our support team{" "}
                    <IgLink onClick={contactSupport}>here</IgLink>.
                  </IgTypography>
                </Grid>
              )}
            </>
          )}
        </Stack>
      }
    />
  );
};
